import {
  BoxGeometry,
  Mesh,
  MeshBasicMaterial,
  OrthographicCamera,
  PerspectiveCamera,
  Vector3,
} from "three";
import { Engine } from "../engine/Engine";
import { getLocalBoundingBox } from "../util/GeometryUtil";
import { SimpleMeter } from "./SimpleMeter";
import { SugarModel } from "./SugarModel";
import { SugarModelConfigurator } from "./SugarModelConfigurator";

export class SugarModelDimensions {
  //private _configurator: SugarModelConfigurator;
  private _meters: SimpleMeter[] = [];
  private _show = false;

  constructor() {
    Engine.activeEngine?.addEventListener("camera-change", (e: any) => {
      this._align(e.detail);
    });
  }

  private _align(camera: OrthographicCamera | PerspectiveCamera) {
    this._meters.forEach((meter) => {
      const meterWorldPosition = meter.getWorldPosition(new Vector3());
      const meterDirection = meter.getWorldDirection(new Vector3());

      const meterWorldDirectionPosition = meterWorldPosition
        .clone()
        .add(meterDirection);

      camera.updateProjectionMatrix();
      camera.updateMatrixWorld();
      camera.updateWorldMatrix(true, true);

      const d = meterWorldDirectionPosition.project(camera);
      const p = meterWorldPosition.project(camera);

      const di = p.x > d.x ? 1 : -1;
      const sd0 = p.clone().sub(d).normalize().multiplyScalar(di);
      const rad = Math.atan2(
        sd0.y,
        sd0.x * (camera instanceof PerspectiveCamera ? camera.aspect : 1)
      );
      meter.labelSprite.material.rotation = rad;
      

      const zoom = camera.userData.controller.getDistance();
      //meter.visible = p.distanceTo(d) * zoom > .25;
      meter.setZoom(zoom);
    });
  }

  update(models: SugarModel[]) {
    const show = this._show;
    this.hide();
    if (show) {
      this.show(models);
    }
  }

  show(models: SugarModel[], showParts: boolean = true) {
    this.hide();
    this._show = true;

    models.forEach((model) => {
      const { size, center, box } = this.getSize(model);

      if (model instanceof SugarModelConfigurator) {
        this._addMeterItemsOfConfigurator(model, center.y + size.y * 0.5 + 0.1, showParts);
      }

      const marginSide = 0.15;
      const marginBck = 0.125;
      const marginTop = 0.2;

      const sizeLabel = this._meters.filter((m) => m.attachTo == model).reduce((acc, m) => acc + m.size, 0);
      const _meterX = new SimpleMeter(size.x);
      _meterX.setDirection(new Vector3(1, 0, 0), new Vector3(0, 1, 0));
      _meterX.setSize(size.x);
      _meterX.setSizeLabel(sizeLabel);
      _meterX.position.setX(center.x - size.x * 0.5);
      _meterX.position.setZ(center.z - size.z * 0.5 - marginBck);
      _meterX.position.setY(center.y + size.y * 0.5 + marginTop);
      model.add(_meterX);
      this._meters.push(_meterX);

      const sizeLabelY = this._meters.filter((m) => m.attachTo == model ).reduce((acc, m) => acc > m.size ? acc : m.size, 0);
      const _meterY = new SimpleMeter(size.y);
      _meterY.setDirection(new Vector3(0, 1, 0), new Vector3(1, 0, 0));
      _meterY.setSize(size.y);
      //_meterY.setSizeLabel(sizeLabelY);
      _meterY.position.setX(center.x - size.x * 0.5 - marginSide);
      _meterY.position.setZ(center.z - size.z * 0.5 - marginSide);
      model.add(_meterY);
      this._meters.push(_meterY);

      //const nextJoint = model.joints?.find((j) => j.outType == "sag");
      //if (nextJoint && nextJoint.binded) return;

      const _meterZ = new SimpleMeter(size.z);
      _meterZ.setDirection(new Vector3(0, 0, -1), new Vector3(0, 1, 0));
      _meterZ.setSize(size.z);
      _meterZ.position.setX(center.x - size.x * 0.5 - marginSide);
      _meterZ.position.setZ(center.z + size.z * 0.5);

      model.add(_meterZ);
      this._meters.push(_meterZ);
      this._align(
        Engine.activeEngine!.activeCamera!.instance as
          | OrthographicCamera
          | PerspectiveCamera
      );
    });

    Engine.activeEngine!.dispatchCustomEvent("request-update");
  }

  private _addMeterItemsOfConfigurator(
    configurator: SugarModelConfigurator,
    yOffset: number,
    showParts: boolean = true
  ) {
    configurator.sideModels.forEach((m) => {
      
      const { size, center } = this.getSize(m);
      //console.log("side model size", size);
      const _meterM = new SimpleMeter(size.x, configurator);
      _meterM.setDirection(new Vector3(1, 0, 0), new Vector3(0, 1, 0));
      _meterM.setSize(size.x);
      _meterM.position.setX(center.x - size.x * 0.5);
      _meterM.position.setZ(center.z - size.z * 0.5 - 0.02);
      _meterM.position.setY(center.y + size.y * 0.5 + 0.1);
      /*m.add(_meterM);
      m.updateWorldMatrix(true, true);
      model.attach(_meterM);
      _meterM.position.y = yOffset; // Adjust position for configurator models*/
      this._meters.push(_meterM);
    });

    configurator.models.forEach((model) => {
      const { size, center } = this.getSize(model);

      const _meterM = new SimpleMeter(size.x, configurator);
      _meterM.setDirection(new Vector3(1, 0, 0), new Vector3(0, 1, 0));
      _meterM.setSize(size.x);
      _meterM.position.setX(center.x - size.x * 0.5);
      _meterM.position.setZ(center.z - size.z * 0.5 - 0.02);
      _meterM.position.setY(center.y + size.y * 0.5 + 0.1);
     
      if(showParts) {
        model.add(_meterM);
        model.updateWorldMatrix(true, true);
        configurator.attach(_meterM);
      }
      _meterM.position.y = yOffset; // Adjust position for configurator models
      this._meters.push(_meterM);

      if (model.data?.id == 60344 || model.data?.id == 87826) {
        const _meterZ = new SimpleMeter(size.z);
        _meterZ.setDirection(new Vector3(0, 0, -1), new Vector3(0, 1, 0));
        _meterZ.setSize(size.z);
        _meterZ.position.setX(center.x + size.x * 0.5 + 0.2);
        _meterZ.position.setZ(center.z + size.z * 0.5);

        
        if(showParts) {
          model.add(_meterZ);
          model.updateWorldMatrix(true, true);
          configurator.attach(_meterZ);
        }
        _meterZ.position.y = yOffset; // Adjust position for configurator models
        this._meters.push(_meterZ);
      }
    });
  }

  hide() {
    this._show = false;
    this._meters.forEach((m) => {
      m.dispose();
      m.removeFromParent();
    });

    this._meters = [];

    Engine.activeEngine!.dispatchCustomEvent("request-update");
  }

  toggle(models: SugarModel[]) {
    if (!this._show) {
      this.show(models);
    } else {
      this.hide();
    }
  }

  getSize(model: SugarModel) {
    const bound = getLocalBoundingBox(model);

    const size = new Vector3();
    bound?.getSize(size);
    const center = new Vector3();
    bound?.getCenter(center);

    const box = new Mesh(
      new BoxGeometry(size.x, size.y, size.z),
      new MeshBasicMaterial({
        color: 0xff0000,
        transparent: true,
        opacity: 0.8,
      })
    );
    box.position.copy(center);

    if (model.data?.x) {
      size.setX(model.data.x / 100);
      center.setX(0);
    }
    if (model.data?.y) {
      size.setY(model.data.y / 100);
      center.setY(model.data.y / 200); // Adjust center for y
    }
    if (model.data?.z) {
      size.setZ(model.data.z / 100);
      center.setZ(0);
    }

    return { size, center, box };
  }
}
